import { ItemCompact } from "@/models/ItemCompact";
import ListRequest, { Filter, OrderType } from "@/models/ListRequest";
import type { Activity, SpeedInputMode } from "@/models/wellness/Activity";
import type { WellnessActivityType } from "@/models/wellness/WellnessActivityType";
import type { WellnessCalculationRequest } from "@/models/wellness/WellnessCalculationRequest";
import type { WellnessCalculationResult } from "@/models/wellness/WellnessCalculationResult";
import type { WellnessItem } from "@/models/wellness/WellnessItem";
import type { WellnessTab } from "@/models/wellness/WellnessTab";
import IngredientsRepository from "@/utils/API/IngredientsRepository";
import RecipesRepository from "@/utils/API/RecipesRepository";
import WellnessRepository from "@/utils/API/WellnessRepository";

export interface WellnessListResponse {
  count: number;
  totalCount: number;
  result: WellnessItem[];
}

export interface WellnessCaloriesResponse {
  type: string;
  id: number;
  calories: number;
}

function normalizeSpeedInputMode(mode: unknown): SpeedInputMode {
  const normalizedMode = typeof mode === "string" ? mode.toLowerCase() : mode;

  switch (mode) {
    case 0:
    case "0":
    case "none":
    case "None":
      return "None";
    case 1:
    case "1":
    case "exact":
    case "Exact":
      return "Exact";
    case 2:
    case "2":
    case "range":
    case "Range":
      return "Range";
    case 3:
    case "3":
    case "maxonly":
    case "max-only":
    case "MaxOnly":
      return "MaxOnly";
    case 4:
    case "4":
    case "minonly":
    case "min-only":
    case "MinOnly":
      return "MinOnly";
    default:
      switch (normalizedMode) {
        case "none":
          return "None";
        case "exact":
          return "Exact";
        case "range":
          return "Range";
        case "maxonly":
        case "max-only":
          return "MaxOnly";
        case "minonly":
        case "min-only":
          return "MinOnly";
        default:
          return "None";
      }
  }
}

export async function listWellnessActivityTypes(): Promise<
  WellnessActivityType[]
> {
  const response = await WellnessRepository.listActivityTypes();
  return response.data;
}

export async function listWellnessActivities(
  activityTypeId?: number,
  search?: string,
): Promise<Activity[]> {
  const response = await WellnessRepository.listActivities(
    activityTypeId,
    search,
  );
  return response.data.map((item) => ({
    id: item.id,
    activityTypeId: item.activityTypeId,
    activityTypeName: item.activityTypeName,
    activityCode: item.activityCode,
    description: item.description,
    icon: item.icon,
    metValue: item.metValue,
    ...(item.isDefault !== undefined ? { isDefault: item.isDefault } : {}),
    speedInputMode: normalizeSpeedInputMode(item.speedInputMode),
    bicyclingComputationApproach: item.bicyclingComputationApproach,
    speedDisplayText: item.speedDisplayText,
    minMph: item.minMph,
    maxMph: item.maxMph,
    exactMph: item.exactMph,
  }));
}

export async function calculateWellnessActivities(
  request: WellnessCalculationRequest,
): Promise<WellnessCalculationResult[]> {
  const response = await WellnessRepository.calculate(request);
  return response.data;
}

function normalizeItems(items: ItemCompact[]): WellnessItem[] {
  return items.map((item) => ({
    id: item.id,
    name: item.name,
    createdOn: item.createdOn,
    updatedOn: item.updatedOn,
    createdUserName: item.createdUserName,
    updatedUserName: item.updatedUserName,
    requiresDensityUpdate: item.requiresDensityUpdate ?? null,
    hasAllergenAnalysis: item.hasAllergenAnalysis ?? null,
    hasAllergen: item.hasAllergen ?? null,
  }));
}

export async function listWellnessItems(
  tab: WellnessTab,
  page: number,
  pageSize: number,
  orderType: "asc" | "desc",
  filters: Filter[] = [],
): Promise<WellnessListResponse> {
  const request = new ListRequest({
    page,
    pageSize,
    filters,
    orders: [
      {
        field: "Name",
        orderType: orderType === "asc" ? OrderType.Asc : OrderType.Desc,
      },
    ],
  });

  if (tab === "customRecipes") {
    const response =
      await IngredientsRepository.listRestaurantIngredients(request);
    return {
      count: response.data.count,
      totalCount: response.data.totalCount,
      result: normalizeItems(response.data.result),
    };
  }

  const response = await RecipesRepository.listReducedRecipes(
    request,
    tab === "recipes",
  );
  return {
    count: response.data.count,
    totalCount: response.data.totalCount,
    result: normalizeItems(response.data.result),
  };
}

export function searchRecipes(term: string) {
  return RecipesRepository.filterRecipesNames(term);
}

export function searchCombinedRecipes(term: string) {
  return RecipesRepository.filterCombinedRecipeName(term);
}

export function searchCustomIngredients(
  pageSize: number,
  term: string,
  orderType: "asc" | "desc",
) {
  return IngredientsRepository.listIngredientSearch(
    new ListRequest({
      page: 1,
      pageSize,
      searchTerm: term,
      orders: [
        {
          field: "Name",
          orderType: orderType === "asc" ? OrderType.Asc : OrderType.Desc,
        },
      ],
    }),
  );
}

export function deleteWellnessItems(tab: WellnessTab, ids: number[]) {
  if (tab === "customRecipes") {
    return IngredientsRepository.deleteRestaurantIngredients({ ids });
  }

  return RecipesRepository.deleteRecipes({ recipeIds: ids });
}

export function listRecipesThatUseIngredient(ingredientId: number) {
  return RecipesRepository.listRecipesThatUseIngredient(ingredientId);
}

function mapTabToCaloriesType(
  tab: WellnessTab,
): "recipe" | "combinedRecipe" | "customIngredient" {
  if (tab === "recipes") {
    return "recipe";
  }

  if (tab === "combinedRecipes") {
    return "combinedRecipe";
  }

  return "customIngredient";
}

export async function getWellnessCalories(
  tab: WellnessTab,
  id: number,
): Promise<number> {
  try {
    const response = await RecipesRepository.getWellnessCalories(
      mapTabToCaloriesType(tab),
      id,
    );
    return Number(response.data?.calories || 0);
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return 0;
    }

    throw error;
  }
}
